<template>
  <f7-page name="achievements">
    <f7-navbar :title="$t('growth.achievements.title')" back-link />
        <AppLayout>
  <template #main>
    <f7-block class="growth-page no-margin no-padding">
      <f7-card class="growth-card summary-card">
        <f7-card-content>
          <div class="text-align-center">
            <div class="t-h2 t-purple t-bold">{{ achievements.length }}</div>
            <div class="t-muted t-small">{{ $t('growth.achievements.earned') }}</div>
          </div>
        </f7-card-content>
      </f7-card>

      <f7-card class="growth-card">
        <f7-card-header>
          <f7-icon f7="rosette" size="22" class="t-purple" />
          <div class="t-bold">{{ $t('growth.achievements.yourBadges') }}</div>
        </f7-card-header>
        <f7-card-content>
          <div v-if="achievements.length === 0" class="t-muted text-align-center py-3">
            {{ $t('growth.achievements.noBadges') }}
          </div>
          <div v-else class="badge-row">
            <f7-chip
              v-for="a in achievements"
              :key="a.id"
              :text="a.badge"
              outline
              class="badge-chip badge-purple"
            >
              <f7-icon f7="star" slot="icon" class="t-purple"></f7-icon>
            </f7-chip>
          </div>
        </f7-card-content>
      </f7-card>

      <f7-card class="growth-card">
        <f7-card-header>
          <f7-icon f7="sparkles" size="22" class="t-gold" />
          <div class="t-bold">{{ $t('growth.achievements.availableBadges') }}</div>
        </f7-card-header>
        <f7-card-content>
          <f7-list class="growth-list">
            <f7-list-item
              v-for="badge in availableBadges"
              :key="badge.id"
              :title="badge.name"
              :subtitle="badge.description"
              :footer="badge.category"
            >
              <f7-icon slot="media" f7="rosette" size="32" class="t-muted-icon"></f7-icon>
            </f7-list-item>
          </f7-list>
          <div v-if="availableBadges.length === 0" class="t-muted text-align-center py-3">
            {{ $t('growth.achievements.noAvailableBadges') }}
          </div>
        </f7-card-content>
      </f7-card>
    </f7-block>
      </template>
</AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Block, f7Card, f7CardHeader, f7CardContent,
  f7List, f7ListItem, f7Icon, f7Chip
} from 'framework7-vue'
import AppLayout from '../components/common/AppLayout.vue'
import { useGrowthStore } from '../js/pinia-stores/growth.js'
import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({ groupId: { type: String, default: '' } })

const growthStore = useGrowthStore()

const groupId = computed(() => props.groupId || '123')
const achievements = computed(() => growthStore.achievements)
const availableBadges = computed(() => growthStore.availableBadges)

onMounted(async () => {
  try {
    await growthStore.fetchAchievements(groupId.value)
  } catch (e) {
    console.warn('Growth achievements unavailable:', e)
  }
})
</script>

<style scoped>
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-blue-light: var(--avocash-blue-light, #ebf0fc);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-terracotta-light: var(--avocash-terracotta-light, #fdecea);
  --avo-leaf: var(--f7-theme-color, #5f8a4a);
  --avo-purple: var(--f7-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--f7-on-surface, #1c1c1e);
}

.t-bold { font-weight: 600; }
.t-h2 { font-size: 28px; line-height: 1.1; }
.t-muted { color: var(--f7-on-surface-variant, #6f7a71); }
.t-small { font-size: 12px; }
.t-purple { color: var(--avo-purple); }
.t-gold { color: var(--avo-gold); }

.summary-card {
  background: linear-gradient(135deg, var(--avo-purple-light), transparent);
}

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }

.badge-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; }
.badge-chip { margin: 0; }
.badge-purple :deep(.chip-label) { color: var(--avo-purple); }
.badge-purple :deep(.chip-outline) { border-color: var(--avo-purple); }

.growth-list :deep(.item-title) { font-size: 14px; }
.growth-list :deep(.item-subtitle) { font-size: 12px; }
.growth-list :deep(.item-footer) { font-size: 11px; }
.t-muted-icon { color: var(--f7-on-surface-variant, #6f7a71); opacity: .75; }
</style>
